import { zIndex } from '@mathflat/common/style'
import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { default as RCTooltip } from 'rc-tooltip'
import React, { useEffect, useState } from 'react'

import { Icon } from '@/component/atoms/Icon'
import type { StyleProps } from '@/types/common'

import S from './Tooltip.style'

type Props = {
  Content: React.ReactNode
  iconText?: string
  iconType?: 'question' | 'exclamation' | 'info'
  place?:
    | 'top'
    | 'topLeft'
    | 'rightTop'
    | 'right'
    | 'rightBottom'
    | 'bottomLeft'
    | 'bottom'
    | 'bottomRight'
    | 'leftBottom'
    | 'left'
    | 'leftTop'
    | 'topRight'
  color?: 'gray' | 'green' | 'warn'
  size?: number
} & StyleProps

const iconName = {
  question: 'help_fill',
  exclamation: 'alert_error_fill',
  info: 'info_fill',
} as const

const Tooltip = ({
  Content,
  iconText,
  iconType = 'info',
  place = 'rightTop',
  color = 'gray',
  size = 20,
  className,
  style,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!isVisible) {
      return
    }

    const handleScroll = (event: Event) => {
      const target = event.target

      const isTooltipScroll = target instanceof HTMLElement && target.closest('.rc-tooltip-content')

      if (!isTooltipScroll) {
        setIsVisible(false)
      }
    }

    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isVisible])

  return (
    <RCTooltip
      placement={place}
      trigger={['click', 'focus']}
      destroyTooltipOnHide
      overlay={<S.Overlay className={color}>{Content}</S.Overlay>}
      onVisibleChange={(visible) => setIsVisible(visible)}
      zIndex={zIndex.Tooltip}
      visible={isVisible}
    >
      <S.Tooltip style={style} className={clsx('tooltip-wrap', color, className)}>
        <div>
          {iconText && <p>{iconText}</p>}
          <Icon
            name={iconName[iconType]}
            size={size}
            color={isVisible ? colors.gray.$700 : colors.gray.$500}
          />
        </div>
      </S.Tooltip>
    </RCTooltip>
  )
}

export default observer(Tooltip)
